import React, { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

// ONE floating control for a video's sound. Replaces the old fragmented pair
// (a mute button + a separate "tap for sound" indicator) with a single element
// that has THREE mutually-exclusive states — so "Sans son" and "Appuyez pour le
// son" can never contradict each other:
//   • hasAudio === false  → static "Sans son" pill (video genuinely has no audio track)
//   • muted (audio present/unknown) → VolumeX; tapping unmutes. In the "full"
//                                      variant it briefly expands with a hint.
//   • unmuted → Volume2; tapping mutes.
// `hasAudio` is the backend ffprobe truth (true | false | null/undefined).
// Unknown (null/undefined) is treated as "audio present" so we NEVER show a
// false "Sans son" — the exact bug this component fixes.
export const SoundControl = ({
  muted,
  onToggle,
  hasAudio = null,
  variant = "full", // "full" (Reels) | "compact" (feed / previews)
  className = "",
  hintLabel = "Appuyez pour le son",
  noAudioLabel = "Sans son",
  muteLabel = "Couper le son",
  testId = "sound-control",
  style = {},
}) => {
  const compact = variant === "compact";
  const silent = hasAudio === false;
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (silent || compact || !muted) { setExpanded(false); return undefined; }
    setExpanded(true);
    const tmr = setTimeout(() => setExpanded(false), 3500);
    return () => clearTimeout(tmr);
  }, [muted, compact, silent]);

  const base =
    "flex items-center gap-1.5 rounded-full text-white font-semibold shadow-lg transition-all duration-300";
  const bg = {
    background: "rgba(0,0,0,0.62)",
    backdropFilter: "blur(6px)",
    border: "1px solid rgba(255,255,255,0.2)",
    ...style,
  };

  // Genuine no-audio video → static, non-interactive badge.
  if (silent) {
    const pad = compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs";
    return (
      <div
        className={`${base} ${pad} ${className} pointer-events-none`}
        style={bg}
        data-testid={testId}
        data-sound-state="silent"
        aria-label={noAudioLabel}
      >
        <VolumeX size={compact ? 14 : 16} className="shrink-0" />
        <span className="whitespace-nowrap">{noAudioLabel}</span>
      </div>
    );
  }

  const showHint = muted && !compact && expanded;
  const pad = compact ? "px-2.5 py-1" : showHint ? "px-4 py-2 text-sm" : "p-2.5";
  const iconSize = compact ? 14 : 18;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.();
      }}
      className={`${base} active:scale-95 ${compact ? "text-[11px]" : ""} ${pad} ${className}`}
      style={bg}
      data-testid={testId}
      data-sound-state={muted ? "muted" : "on"}
      aria-label={muted ? hintLabel : muteLabel}
    >
      {muted ? (
        <VolumeX size={iconSize} className="shrink-0" />
      ) : (
        <Volume2 size={iconSize} className="shrink-0" />
      )}
      {showHint && <span className="whitespace-nowrap">{hintLabel}</span>}
    </button>
  );
};

export default SoundControl;
